import { Modal } from 'react-bootstrap';
import { MdCheck, MdContentCopy } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { useCopyToClipboard } from '@src/utils/hooks/useCopyToClipboard';

interface RunScanModalProps {
  command: string;
  onHide: () => void;
}

/** Shows the `/alexa-visibility-scan` command to run and how a scan starts. */
export const RunScanModal = ({ command, onHide }: RunScanModalProps) => {
  const { copyToClipboard, copiedText } = useCopyToClipboard();
  const copied = copiedText === command;

  return (
    <Modal show centered size='lg' onHide={onHide} data-testid='run-scan-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Run a scan from your machine</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Scans now run in your own browser, signed in to your Amazon account, instead of on a
          server. Results upload to this page when the scan finishes.
        </p>
        <ol>
          <li>
            Open Claude Code in the <code>bpm-lambdas</code> repo, or open Cowork with the Alexa
            Visibility skill installed.
          </li>
          <li>Run this command:</li>
        </ol>
        <pre className='aars-scan-command' data-testid='scan-command'>
          {command}
        </pre>
        <ol start={3}>
          <li>
            In Claude Code, a Chrome window opens. In Cowork, Claude cannot open Chrome on your
            computer, so it replies with a command instead: paste it into Terminal (Mac) or
            PowerShell (Windows). You need Node 18+ and Google Chrome installed.
          </li>
          <li>
            The first time, sign in to Amazon, and to this app when a tab for it opens; both are
            remembered after that. Leave the window alone until the scan finishes (about a minute
            per question).
          </li>
        </ol>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Close
        </Button>
        <Button
          data-testid='copy-scan-command'
          leadingElement={copied ? <MdCheck /> : <MdContentCopy />}
          onClick={() => copyToClipboard(command)}
        >
          {copied ? 'Copied' : 'Copy command'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
